import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyPhoneMask } from '../utils/masks';
import { motion } from 'framer-motion';
import { Trash2, Edit, X, Plus, CheckCircle, Ban, Users, Clock, ArrowLeft, FileText, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { User, UserRole } from '../contexts/AuthContext';

export const UserManagement = () => {
  const navigate = useNavigate();
  const { users, updateUserStatus, deleteUser, adminCreateUser, adminUpdateUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [hasExistingRegistration, setHasExistingRegistration] = useState(true);

  const handleGenerateCertificate = (user: User) => {
    if (!user.lgpdAcceptedAt) return;
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) return;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Certificado de Aceite LGPD - ${user.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
            h1 { color: #2D1E4B; border-bottom: 2px solid #2D1E4B; padding-bottom: 10px; }
            .content { margin-top: 20px; }
            .item { margin-bottom: 15px; }
            .label { font-weight: bold; color: #555; }
            .footer { margin-top: 50px; font-size: 12px; color: #777; border-top: 1px solid #ccc; padding-top: 20px; }
          </style>
        </head>
        <body>
          <h1>Certificado de Aceite de Termos e LGPD</h1>
          <p>Este documento certifica o aceite eletrônico das Políticas de Privacidade e Termos de Uso da TechTennis pelo usuário abaixo qualificado.</p>
          
          <div class="content">
            <div class="item"><span class="label">Nome Completo:</span> ${user.name}</div>
            <div class="item"><span class="label">E-mail:</span> ${user.email}</div>
            <div class="item"><span class="label">ID no Sistema:</span> ${user.id}</div>
            
            <h3>Dados Técnicos do Aceite</h3>
            <div class="item"><span class="label">Data e Hora (Timestamp):</span> ${new Date(user.lgpdAcceptedAt).toLocaleString('pt-BR')}</div>
            <div class="item"><span class="label">Endereço IP:</span> ${user.lgpdIp || 'Não registrado na versão anterior'}</div>
            <div class="item"><span class="label">Dispositivo / Navegador:</span> ${user.lgpdUserAgent || 'Não registrado na versão anterior'}</div>
            <div class="item"><span class="label">Versão do Documento:</span> ${user.lgpdVersion || 'v1.0 (Base)'}</div>
          </div>
          
          <div class="footer">
            Documento gerado eletronicamente pelo sistema TechTennis em ${new Date().toLocaleString('pt-BR')}.<br/>
            A validade deste documento pode ser auditada nos logs do servidor.
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setHasExistingRegistration(!!user.numericId || user.status === 'pending');
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setHasExistingRegistration(true);
    setIsModalOpen(true);
  };

  const filteredUsers = users.filter(u => {
    const term = searchTerm.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const nameMatch = u.name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(term);
    const emailMatch = u.email.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(term);
    const idMatch = u.numericId ? u.numericId.toString().includes(term) : false;
    return nameMatch || emailMatch || idMatch;
  });

  const activeUsers = filteredUsers.filter(u => u.status !== 'pending');
  const pendingUsers = filteredUsers.filter(u => u.status === 'pending');

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-panel" style={{ padding: '32px', border: '1px solid var(--border-light)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => navigate('/')} style={{ background: 'var(--bg-panel)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 800, color: 'white', margin: 0 }}>Gestão de Usuários</h2>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerencie os acessos ao sistema</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.6)' }} />
            <input 
              type="text" 
              placeholder="Buscar por nome, ID ou email..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '10px 12px 10px 40px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: 'white', outline: 'none', width: '280px' }} 
            />
          </div>
          <button onClick={handleAdd} style={{ background: 'var(--primary-color)', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <Plus size={18} /> Adicionar Usuário
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <button 
          onClick={() => setActiveTab('active')}
          style={{ padding: '12px 24px', background: 'none', border: 'none', borderBottom: activeTab === 'active' ? '2px solid var(--primary-color)' : '2px solid transparent', color: activeTab === 'active' ? 'var(--primary-color)' : 'white', fontWeight: 700, cursor: 'pointer', marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Users size={18} /> Usuários Existentes ({activeUsers.length})
        </button>
        <button 
          onClick={() => setActiveTab('pending')}
          style={{ padding: '12px 24px', background: 'none', border: 'none', borderBottom: activeTab === 'pending' ? '2px solid var(--primary-color)' : '2px solid transparent', color: activeTab === 'pending' ? 'var(--primary-color)' : 'white', fontWeight: 700, cursor: 'pointer', marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Clock size={18} /> Solicitações Pendentes ({pendingUsers.length})
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', color: '#6B7280', fontSize: '13px', textTransform: 'uppercase' }}>
              <th style={{ padding: '16px 24px' }}>Usuário</th>
              <th style={{ padding: '16px 24px' }}>Contato</th>
              <th style={{ padding: '16px 24px' }}>Perfil / Acesso</th>
              <th style={{ padding: '16px 24px', textAlign: 'center' }}>Vínculo (ID TechTennis)</th>
              <th style={{ padding: '16px 24px', textAlign: 'center' }}>Aceite LGPD</th>
              {activeTab === 'active' && <th style={{ padding: '16px 24px', textAlign: 'center' }}>Status</th>}
              <th style={{ padding: '16px 24px', textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {(activeTab === 'active' ? activeUsers : pendingUsers).map(user => (
              <tr key={user.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 700, color: '#2D1E4B' }}>{user.name}</span>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>{user.email}</span>
                </td>
                <td style={{ padding: '16px 24px', color: '#4B5563', fontSize: '14px' }}>
                  {user.phone ? applyPhoneMask(user.phone) : '-'}
                </td>
                <td style={{ padding: '16px 24px' }}>
                  <span style={{ 
                    padding: '4px 10px', borderRadius: '100px', fontSize: '12px', fontWeight: 700,
                    background: user.role === 'ADMIN' ? '#FEE2E2' : user.role === 'GESTOR_COMPETICAO' ? '#FFEDD5' : user.role.includes('ENCORDOADOR') ? '#E0E7FF' : user.role.includes('PROFESSOR') ? '#FEF3C7' : '#DCFCE7',
                    color: user.role === 'ADMIN' ? '#991B1B' : user.role === 'GESTOR_COMPETICAO' ? '#9A3412' : user.role.includes('ENCORDOADOR') ? '#3730A3' : user.role.includes('PROFESSOR') ? '#92400E' : '#166534'
                  }}>
                    {user.role === 'ENCORDOADOR_MASTER' ? 'ENC. MASTER' : user.role === 'GESTOR_COMPETICAO' ? 'GESTOR (TORNEIOS)' : user.role}
                  </span>
                </td>
                <td style={{ padding: '16px 24px', textAlign: 'center', fontWeight: 'bold', color: user.numericId ? '#2563EB' : '#9CA3AF' }}>
                   {user.numericId ? `#${user.numericId}` : 'Não Vinculado'}
                </td>
                <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                  {user.lgpdAcceptedAt ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span style={{ color: '#166534', fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CheckCircle size={14} /> Aceito
                      </span>
                      <span style={{ fontSize: '11px', color: '#6B7280' }}>
                        {new Date(user.lgpdAcceptedAt).toLocaleDateString('pt-BR')} às {new Date(user.lgpdAcceptedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ) : (
                    <span style={{ color: '#9CA3AF', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <Clock size={14} /> Pendente
                    </span>
                  )}
                </td>
                {activeTab === 'active' && (
                  <td style={{ padding: '16px 24px', textAlign: 'center' }}>
                    <span style={{ 
                      padding: '4px 10px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, display: 'inline-block',
                      background: user.status === 'active' ? '#DCFCE7' : '#FEE2E2',
                      color: user.status === 'active' ? '#166534' : '#991B1B'
                    }}>
                      {user.status === 'active' ? 'Ativo' : 'Bloqueado'}
                    </span>
                  </td>
                )}
                <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      {activeTab === 'active' ? (
                        <button onClick={() => updateUserStatus(user.id, user.status === 'blocked' ? 'active' : 'blocked', undefined, user.numericId)} style={{ background: 'transparent', border: 'none', color: user.status === 'blocked' ? '#10B981' : '#F59E0B', cursor: 'pointer' }} title={user.status === 'blocked' ? 'Desbloquear Usuário' : 'Bloquear Usuário'}>
                           {user.status === 'blocked' ? <CheckCircle size={18} /> : <Ban size={18} />}
                        </button>
                      ) : (
                        <button onClick={() => handleEdit(user)} style={{ background: '#10B981', color: 'white', padding: '6px 12px', borderRadius: '6px', fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                           <CheckCircle size={14} /> Aprovar / Vincular
                        </button>
                      )}
                      
                      {activeTab === 'active' && (
                        <>
                          {user.lgpdAcceptedAt && (
                             <button onClick={() => handleGenerateCertificate(user)} style={{ background: 'transparent', border: 'none', color: '#3B82F6', cursor: 'pointer' }} title="Gerar Certificado LGPD (PDF)"><FileText size={18} /></button>
                          )}
                          <button onClick={() => handleEdit(user)} style={{ background: 'transparent', border: 'none', color: '#6B7280', cursor: 'pointer' }} title="Editar"><Edit size={18} /></button>
                        </>
                      )}
                      <button onClick={() => window.confirm('Deseja deletar este usuário do sistema?') && deleteUser(user.id)} style={{ background: 'transparent', border: 'none', color: '#EF4444', cursor: 'pointer' }} title="Excluir Definitivamente"><Trash2 size={18} /></button>
                    </div>
                </td>
              </tr>
            ))}
            {(activeTab === 'active' ? activeUsers : pendingUsers).length === 0 && (
               <tr><td colSpan={10} style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Nenhum usuário encontrado nesta aba.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,12,60,0.8)', backdropFilter: 'blur(8px)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} style={{ width: '100%', maxWidth: '600px', background: 'white', borderRadius: '24px', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <form onSubmit={async (e) => {
                    e.preventDefault();
                    const fd = new FormData(e.currentTarget);
                    const newStatus = fd.get('status') as User['status'];
                    
                    if (hasExistingRegistration && !fd.get('numericId')) {
                        alert('Como este usuário já tem cadastro anterior, é obrigatório informar o ID TechTennis para aprovar e vincular!');
                        return;
                    }

                    const userData = {
                        name: fd.get('name') as string,
                        email: fd.get('email') as string,
                        password: (fd.get('password') as string) || undefined,
                        phone: fd.get('phone') as string,
                        role: fd.get('role') as UserRole,
                        assignedClub: fd.get('assignedClub') as string,
                        status: newStatus,
                        numericId: fd.get('numericId') ? parseInt(fd.get('numericId') as string, 10) : undefined
                    };
                    if (selectedUser) {
                        await adminUpdateUser(selectedUser.id, userData);
                    } else {
                        await adminCreateUser(userData);
                    }

                    setIsModalOpen(false);
                }} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    <div style={{ background: 'var(--primary-color)', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <h3 style={{ color: 'var(--text-dark)', fontSize: '20px', fontWeight: 700, margin: 0 }}>
                           {selectedUser?.status === 'pending' ? 'Aprovar / Vincular Acesso' : (selectedUser ? 'Editar Usuário' : 'Adicionar Usuário')}
                        </h3>
                        <button type="button" onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', color: 'var(--text-dark)', cursor: 'pointer' }}><X size={24} /></button>
                    </div>
                    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', flex: 1 }}>
                        
                        <div style={{ background: '#F0F9FF', padding: '16px', borderRadius: '12px', border: '1px solid #BAE6FD' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#0369A1', fontWeight: 800, fontSize: '14px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={hasExistingRegistration} onChange={e => setHasExistingRegistration(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                            Usuário JÁ POSSUI cadastro na base de dados antiga
                          </label>
                          <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#0284C7' }}>
                            {hasExistingRegistration 
                               ? 'Obrigatório! Preencha o ID TechTennis antigo para resgatar e vincular os dados.'
                               : 'Para usuários NOVOS, o ID TechTennis pode ser gerado automaticamente pelo sistema no primeiro cadastro.'}
                          </p>
                          <input type="number" required={hasExistingRegistration} name="numericId" defaultValue={selectedUser?.numericId || ''} placeholder="Ex: 162310" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #7DD3FC', color: '#0369A1', background: 'white', fontWeight: 700 }} />
                        </div>

                        <div><label style={{ display: 'block', marginBottom: '8px', color: '#4B5563', fontWeight: 600, fontSize: '14px' }}>Nome Completo *</label><input required name="name" defaultValue={selectedUser?.name || ''} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', color: '#111827' }} /></div>
                        <div><label style={{ display: 'block', marginBottom: '8px', color: '#4B5563', fontWeight: 600, fontSize: '14px' }}>Email *</label><input required type="email" name="email" defaultValue={selectedUser?.email || ''} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', color: '#111827' }} /></div>
                        <div><label style={{ display: 'block', marginBottom: '8px', color: '#4B5563', fontWeight: 600, fontSize: '14px' }}>Senha de Acesso {selectedUser ? '(Deixe em branco para manter)' : '*'}</label><input required={!selectedUser} type="text" name="password" defaultValue={selectedUser ? '' : '123456'} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', color: '#111827' }} /></div>
                        <div><label style={{ display: 'block', marginBottom: '8px', color: '#4B5563', fontWeight: 600, fontSize: '14px' }}>Telefone</label><input name="phone" onChange={(e) => e.target.value = applyPhoneMask(e.target.value)} defaultValue={selectedUser?.phone ? applyPhoneMask(selectedUser.phone) : ''} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', color: '#111827' }} /></div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', color: '#4B5563', fontWeight: 600, fontSize: '14px' }}>Perfil de Acesso ao Painel</label>
                                <select name="role" defaultValue={selectedUser?.role || 'CLIENTE'} required style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', color: '#111827', background: 'white' }}>
                                    <option value="CLIENTE">Painel Restrito (Apenas Cliente)</option>
                                    <option value="PROFESSOR">Painel de Professor Base</option>
                                    <option value="PROFESSOR_PREMIUM">Painel de Professor Premium</option>
                                    <option value="ENCORDOADOR">Encordoador / Vendedor</option>
                                    <option value="ENCORDOADOR_MASTER">Encordoador Master</option>
                                    <option value="GESTOR_COMPETICAO">Gestor de Torneios/Ranking</option>
                                    <option value="ADMIN">Administrador Geral</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', color: '#4B5563', fontWeight: 600, fontSize: '14px' }}>Clube/Residencial Atribuído</label>
                                <input name="assignedClub" defaultValue={(selectedUser as any)?.assignedClub || ''} placeholder="Ex: Clube Pinheiros" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', color: '#111827', background: 'white' }} />
                                <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#6B7280' }}>*Apenas para Gestor de Competição</p>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', color: '#4B5563', fontWeight: 600, fontSize: '14px' }}>Status da Conta</label>
                                <select name="status" defaultValue={selectedUser?.status === 'pending' ? 'active' : (selectedUser?.status || 'active')} required style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB', color: '#111827', background: 'white', fontWeight: 700 }}>
                                    <option value="active">Liberado (Ativo)</option>
                                    <option value="pending">Pendente de Avaliação</option>
                                    <option value="blocked">Bloqueado Temporariamente</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div style={{ padding: '24px', background: '#F9FAFB', display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #E5E7EB', flexShrink: 0 }}>
                        <button type="button" onClick={() => setIsModalOpen(false)} style={{ padding: '12px 24px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', color: '#374151' }}>Cancelar</button>
                        <button type="submit" className="button-primary" style={{ padding: '12px 24px' }}>
                           {selectedUser?.status === 'pending' ? 'Confirmar Aprovação' : 'Salvar Alterações'}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
      )}
    </motion.div>
  );
};
